import { memo, ReactNode, useCallback } from 'react'

import { IContact } from 'clickNgo/entities/contact'
import { IPartner } from 'clickNgo/entities/partner/models'
import { ModalDialog } from 'core-components/library/modalDialog'

import { ContactCreatorContent } from './ContactCreatorContent'
import { WelcomeMessage } from './models'

import styles from './ContactCreator.module.scss'

type Props = WelcomeMessage & {
  /* Settings */
  isVisible: boolean
  /* Data */
  partner: IPartner
  /* Optional */
  initialValues?: Partial<IContact>
  size?: 'lg' | 'xl'
  /* Handlers */
  onClose: () => void
  onCreate: (contact: IContact) => Promise<void> | void
  onReactivate: (contact: IContact) => void
  source?: string
}

export const ContactCreator = memo<Props>(
  ({
    partner,
    isVisible,
    onClose,
    onCreate,
    onReactivate,
    initialValues,
    welcomeMessage,
    welcomeBtnText,
    welcomeTitle,
    size = 'lg',
    source,
  }) => {
    const isPartnerExist = Boolean(partner)

    /* -------------------------- Handlers -------------------------- */

    const handleOnClose = useCallback(() => {
      if (onClose) {
        onClose()
      }
    }, [onClose])

    /* -------------------------- Render -------------------------- */

    const renderContent = useCallback(
      () => (
        <ContactCreatorContent
          onClose={handleOnClose}
          partner={partner}
          onCreate={onCreate}
          onReactivate={onReactivate}
          initialValues={initialValues}
          welcomeMessage={welcomeMessage}
          welcomeBtnText={welcomeBtnText}
          welcomeTitle={welcomeTitle}
          source={source}
        />
      ),
      [
        handleOnClose,
        partner,
        onCreate,
        onReactivate,
        initialValues,
        welcomeMessage,
        welcomeBtnText,
        welcomeTitle,
        source,
      ]
    )

    if (isVisible && isPartnerExist) {
      return (
        <ModalDialog
          classes={{
            overlay: styles.modalOverlay,
            window: styles.modalWindow,
            body: styles.modalBody,
          }}
          onClose={handleOnClose}
          content={renderContent}
          closeOnOutsideClick={false}
          show={true}
          size={size}
        />
      )
    }

    return null
  }
)

ContactCreator.displayName = 'ContactCreator'
